/**
 * Création d'un client avec ses sites et ses machines (décision du 12 octobre
 * 2026) : une seule requête, enregistrée d'un bloc. L'API applique ces bornes ;
 * l'interface les annonce avant l'envoi.
 */
import { z } from 'zod';

export const CUSTOMER_CREATE_MAX_SITES = 20;
export const CUSTOMER_CREATE_MAX_EQUIPMENTS = 50;

export const customerSiteDraftSchema = z.object({
  label: z.string().trim().min(1).max(200),
  address: z.string().trim().min(3).max(500),
});

export const customerEquipmentDraftSchema = z.object({
  category: z.string().trim().min(1).max(100),
  brand: z.string().trim().max(100).optional(),
  model: z.string().trim().max(100).optional(),
  serialNumber: z.string().trim().max(200).optional(),
});

/**
 * Machine saisie à la création du client. Choisie dans le catalogue de
 * l'entreprise, elle en copie les valeurs et garde la référence de la fiche
 * (décision du 12 octobre 2026) ; le fluide n'est saisi qu'avec le module.
 */
export const customerCreateEquipmentSchema = customerEquipmentDraftSchema.extend({
  machineModelId: z.guid().optional(),
  fluidType: z.string().trim().max(40).optional(),
  fluidChargeGrams: z.number().int().min(0).max(1_000_000).optional(),
});

export const customerCreateSiteSchema = customerSiteDraftSchema.extend({
  equipments: z.array(customerCreateEquipmentSchema).max(CUSTOMER_CREATE_MAX_EQUIPMENTS).default([]),
});

/** Sites d'un nouveau client : au moins un, au plus 20, 50 machines en tout. */
export const customerCreateSitesSchema = z
  .array(customerCreateSiteSchema)
  .min(1)
  .max(CUSTOMER_CREATE_MAX_SITES)
  .superRefine((sites, ctx) => {
    const total = sites.reduce((sum, site) => sum + site.equipments.length, 0);
    if (total > CUSTOMER_CREATE_MAX_EQUIPMENTS) {
      ctx.addIssue({
        code: 'custom',
        message: `Au plus ${CUSTOMER_CREATE_MAX_EQUIPMENTS} machines par enregistrement.`,
      });
    }
    // Le numéro de série est unique dans l'entreprise : un doublon dans la même
    // saisie est une faute de frappe à corriger avant l'envoi.
    const seen = new Set<string>();
    sites.forEach((site, s) =>
      site.equipments.forEach((equipment, e) => {
        const serial = equipment.serialNumber?.trim();
        if (!serial) return;
        if (seen.has(serial)) {
          ctx.addIssue({
            code: 'custom',
            path: [s, 'equipments', e, 'serialNumber'],
            message: `Le numéro de série ${serial} est saisi deux fois.`,
          });
        }
        seen.add(serial);
      }),
    );
  });

export type CustomerSiteDraft = z.infer<typeof customerSiteDraftSchema>;
export type CustomerEquipmentDraft = z.infer<typeof customerEquipmentDraftSchema>;
export type CustomerCreateEquipment = z.infer<typeof customerCreateEquipmentSchema>;
export type CustomerCreateSite = z.infer<typeof customerCreateSiteSchema>;
